import { useEffect, useRef } from 'react';
import { Router } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { SnackbarProvider } from 'notistack';
import { ErrorBoundary } from 'react-error-boundary';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';

import routes from 'src/routes/index';
import { renderRoutes } from 'src/routes/index';
import useAuth from 'src/hooks/useAuth';
import history from 'src/history';
import ErrorFallback, { myErrorHandler } from './ErrorFallback';
import ContentManager from 'src/componentes/content-manager';
import { ConfiguracaoSistemaProvider } from 'src/contexts/ConfiguracaoSistemaContext';

import './utils/microsoft-date-support';
import './styles/bootstrap.scss';
import './styles/app.scss';

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  sendDefaultPii: true,
  tracesSampleRate: 1.0,
  beforeSend(event, hint) {
    const error = hint?.originalException;

    if (!error) return event;

    if (error?.message === 'cancelation token') return null;
    if (error?.name === 'CanceledError') return null;
    if (error?.name === 'AbortError') return null;

    return event;
  },
});

ReactGA.initialize('G-K9HDLGQN9D', {
  cookieDomain: 'auto',
  // debug: true,
});

const App = () => {
  const auth = useAuth();
  const sentInitialPV = useRef(false);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  useEffect(() => {
    const currentModule = auth.module?.codigo;

    if (currentModule) {
      Sentry.setTag('currentModule', currentModule);
    } else {
      Sentry.setTag('currentModule', undefined);
    }
  }, [auth.module]);

  useEffect(() => {
    if (auth.user?.id) {
      const subdomain = window.location.hostname.split('.')[0];
      const userId = `${auth.user?.id}_${auth.user?.idIntegracao}_${subdomain}`;
      const userArea = `${auth.user?.areaUsuario?.id}_${auth.user?.areaUsuario?.idIntegracao}_${subdomain}`;

      ReactGA.set({ userId: userId });
      ReactGA.gtag('set', 'user_properties', {
        custom_user_id: userId,
        custom_user_area: userArea,
      });

      Sentry.setUser({
        id: auth.user?.id,
        email: auth.user?.email,
        username: auth.user?.idIntegracao,
      });
    } else {
      ReactGA.set({ userId: undefined });
      ReactGA.gtag('set', 'user_properties', {
        custom_user_id: null,
        custom_user_area: null,
      });
      Sentry.setUser(null);
    }

    if (!sentInitialPV.current) {
      const page = window.location.pathname + window.location.search;
      ReactGA.send({
        hitType: 'pageview',
        page,
        title: document.title,
        location: window.location.origin + page,
      });
      sentInitialPV.current = true;
    }

    const unlisten = history.listen(({ pathname, search }) => {
      const page = pathname + (search || '');
      ReactGA.send({
        hitType: 'pageview',
        page,
        title: document.title,
        location: window.location.origin + page,
      });
    });

    return () => unlisten();
  }, [auth.user]);

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={auth.reCaptchaSiteKey}
      container={{
        element: 'actio-recaptcha',
        parameters: { badge: 'bottomright' },
      }}
    >
      <ConfiguracaoSistemaProvider user={auth.user}>
        <IntlProvider
          locale={auth.user ? auth.user.idioma : 'en-us'}
          messages={auth.resources && auth.resources.messages}
        >
          <SnackbarProvider
            classes={{ containerRoot: 'actioSnackbarContainerRoot' }}
            maxSnack={1}
          >
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={(error, info) => {
                myErrorHandler(error, info);
                Sentry.captureException(error, {
                  extra: { componentStack: info?.componentStack },
                });
              }}
            >
              <Router history={history}>{renderRoutes(routes)}</Router>
            </ErrorBoundary>
            <ContentManager name="default2" />
          </SnackbarProvider>
        </IntlProvider>
      </ConfiguracaoSistemaProvider>
    </GoogleReCaptchaProvider>
  );
};

export default App;
